// custom hook to get rto and insurance pricing data for selected variant and city
"use client";

import { useState, useEffect, useRef } from "react";
import { getOnRoadPrice } from "@/utils/onRoadPrice";
import { getCityBySlug, getGlobalSettings } from "@/app/utilities/apis";
import { getLocationCookie, getLocationString } from "@/app/utilities/locationCookie";

export interface UsePricingDataOptions {
    model: any;
    selectedVariant: any;
    price: number;
    isPriceInCityRoute?: boolean;
    prefetchedRtoResult?: any;
    prefetchedInsuranceResult?: any;
    prefetchedVariantId?: string | number;
    prefetchedCity?: string;
    /** Additional values that should trigger a pricing recalculation */
    extraDeps?: unknown[];
    /** When true and no prefetchedCity, sync selected city from location cookie on mount */
    syncCityFromCookieOnMount?: boolean;
}

export interface UsePricingDataReturn {
    rtoResult: any;
    insuranceResult: any;
    isRtoLoading: boolean;
    isInsuranceLoading: boolean;
    selectedCity: string;
    setSelectedCity: (city: string) => void;
}

export function useRTOPricingData({
    model,
    selectedVariant,
    price,
    isPriceInCityRoute = false,
    prefetchedRtoResult,
    prefetchedInsuranceResult,
    prefetchedVariantId,
    prefetchedCity,
    extraDeps = [],
    syncCityFromCookieOnMount = false,
}: UsePricingDataOptions): UsePricingDataReturn {
    const [rtoResult, setRtoResult] = useState<any>(prefetchedRtoResult ?? null);
    const [insuranceResult, setInsuranceResult] = useState<any>(prefetchedInsuranceResult ?? null);
    const [isRtoLoading, setIsRtoLoading] = useState<boolean>(!prefetchedRtoResult && isPriceInCityRoute);
    const [isInsuranceLoading, setIsInsuranceLoading] = useState<boolean>(!prefetchedInsuranceResult && isPriceInCityRoute);
    const [selectedCity, setSelectedCity] = useState<string>(
        () => prefetchedCity || `${model?.locationInfo?.name || ""}`,
    );

    const locationChangedRef = useRef(false);

    const fetchCityData = async () => {
        const loc = getLocationCookie ? getLocationCookie() : null;
        if (loc?.slug) {
            const cityData = await getCityBySlug(loc.slug).catch(() => null);
            if (cityData) return cityData;
        }
        if (model?.locationInfo?.state?.rtoSlabs) return model.locationInfo;
        try {
            const def = await getGlobalSettings();
            return def || null;
        } catch {
            return null;
        }
    };

    useEffect(() => {
        if (!syncCityFromCookieOnMount || prefetchedCity) return;
        const loc = getLocationString();
        if (loc) setSelectedCity(loc);
    }, [prefetchedCity, syncCityFromCookieOnMount]);

    useEffect(() => {
        let mounted = true;

        const computePricing = async () => {
            try {
                const shouldCalculateCharges = Boolean(selectedCity) || isPriceInCityRoute;

                if (!shouldCalculateCharges) {
                    setRtoResult(null);
                    setInsuranceResult(null);
                    setIsRtoLoading(false);
                    setIsInsuranceLoading(false);
                    return;
                }

                const isInitialVariant =
                    prefetchedVariantId === undefined ||
                    String(prefetchedVariantId) === String(selectedVariant?._id || selectedVariant?.name);

                if (prefetchedRtoResult !== undefined && isInitialVariant && !locationChangedRef.current) {
                    setRtoResult(prefetchedRtoResult);
                    setIsRtoLoading(false);
                    if (prefetchedInsuranceResult !== undefined) {
                        setInsuranceResult(prefetchedInsuranceResult);
                        setIsInsuranceLoading(false);
                        return;
                    }
                }

                setIsRtoLoading(true);
                setIsInsuranceLoading(true);
                const cityData = await fetchCityData();
                const nextCity = cityData ? `${cityData.name}` : "";
                setSelectedCity(nextCity);

                if (!mounted || !cityData) {
                    setRtoResult(null);
                    setInsuranceResult(null);
                    setIsRtoLoading(false);
                    setIsInsuranceLoading(false);
                    return;
                }

                const result = getOnRoadPrice(selectedVariant, cityData);
                setRtoResult(result?.rtoEstimate ?? null);
                setInsuranceResult(result?.insuranceEstimate ?? null);
                setIsRtoLoading(false);
                setIsInsuranceLoading(false);
            } catch {
                setRtoResult(null);
                setInsuranceResult(null);
                setIsRtoLoading(false);
                setIsInsuranceLoading(false);
            }
        };

        computePricing();
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedVariant, selectedCity, price, model?.locationInfo?.name, ...extraDeps]);

    useEffect(() => {
        const handler = (event: Event) => {
            locationChangedRef.current = true;
            const display = (event as CustomEvent)?.detail?.displayString;
            if (display) {
                setSelectedCity(display);
            } else {
                const match = document.cookie.match(/(?:^|; )location=([^;]*)/);
                if (match?.[1]) setSelectedCity(decodeURIComponent(match[1]));
            }
        };

        window.addEventListener("location-updated", handler as EventListener);
        return () => window.removeEventListener("location-updated", handler as EventListener);
    }, []);

    return {
        rtoResult,
        insuranceResult,
        isRtoLoading,
        isInsuranceLoading,
        selectedCity,
        setSelectedCity,
    };
}
